// @flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import ScrollFetcher from 'app/common/react/components/ScrollFetcher/ScrollFetcher';
import IndexManager, {
  type SectionRowIndex,
} from 'app/common/react/components/common/SectionList/IndexManager';
import { Box, Touchable } from 'gestalt';

// $FlowFixMe Flow v0.104.0 upgrade supression.
export type Item = *;

// the shape of the props to be passed to the item render function
export type ItemRenderProps = {
  hovered: boolean,
  item: Item,
  sectionProps?: Object,
};

export type Section = {
  // list of items to render
  items: Array<Item>,
  // custom item rendering for this specific section
  renderItem?: (props: ItemRenderProps) => ?React.Element<any>,
  // custom props for rendering per section & is also passed to the item for rendering
  customProps?: {
    // one of the possible props is a function to determine if the current item should not be selectable
    isDisabled?: (item: Item) => boolean,
    // $FlowFixMe Flow v0.104.0 upgrade supression.
  } & *,
  // title of the section
  title?: string,
};

type Props = {
  // disabled state prevents any interaction on the elements
  disabled?: boolean,
  // scroll fetch support
  fetchMore?: () => void,
  // This is true if the flexbox should grow for the list
  grow?: boolean,
  // manages the hover/selection state of the items
  indexManager: IndexManager,
  // Setting the maximum height for the list
  maxHeight?: number | string,
  // called when an item in the list is selected
  onSelectItem: (item: Item) => void,
  // common render method for rendering items
  renderItem?: (props: ItemRenderProps) => ?React.Element<any>,
  // common render method for section headers
  renderSectionHeader?: (section: Section, index: number) => ?React.Element<any>,
  // list of sections to render
  sections: Array<Section>,
};

type State = {|
  selectedIndex?: SectionRowIndex,
  selectionMovedByKeyboard: boolean,
|};

/**
 * Similar usage to the ReactNative SectionList https://facebook.github.io/react-native/docs/sectionlist.html
 * <SectionList
    renderItem={({item}) => <ListItem title={item.title} />}
    renderSectionHeader={({section}) => <H1 title={section.title} />}
    sections={[ // homogenous rendering between sections
      {items: [...], title: ...},
      {items: [...], title: ...},
      {items: [...], title: ...},
    ]}
  />

  <SectionList
    sections={[ // heterogeneous rendering between sections
      {items: [...], title: ..., renderItem: ...},
      {items: [...], title: ..., renderItem: ...},
      {items: [...], title: ..., renderItem: ...},
    ]}
  />
 */

export default class SectionList extends React.Component<Props, State> {
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  constructor(props: Props, context: *) {
    super(props, context);
    const { indexManager } = this.props;
    this.state = {
      selectedIndex: indexManager.getIndex(),
      selectionMovedByKeyboard: false,
    };
    indexManager.setUpdateCallback(this.handleIndexUpdate);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { indexManager } = this.props;
    if (indexManager !== nextProps.indexManager) {
      nextProps.indexManager.setUpdateCallback(this.handleIndexUpdate);
    }
  }

  componentDidUpdate() {
    // Ensure selected index is visible
    const { containerRef } = this;
    if (this.selectedItemRef && this.state.selectionMovedByKeyboard && containerRef) {
      const rowDOMNode = ReactDOM.findDOMNode(this.selectedItemRef);
      if (!(rowDOMNode instanceof HTMLElement)) {
        return;
      }
      const rowBoundingClientRect = rowDOMNode.getBoundingClientRect();
      const rowTop = rowBoundingClientRect.top;
      const rowBottom = rowBoundingClientRect.bottom;

      const containerBoundingClientRect = containerRef.getBoundingClientRect();
      const listTop = containerBoundingClientRect.top;
      const listBottom = containerBoundingClientRect.bottom;

      if (rowBottom >= listBottom) {
        rowDOMNode.scrollIntoView(false);
      } else if (rowTop <= listTop) {
        rowDOMNode.scrollIntoView(true);
      }
    }
  }

  setSelectedItemRef = (ref: ?HTMLDivElement) => {
    this.selectedItemRef = ref;
  };

  containerRef: ?HTMLDivElement;
  selectedItemRef: ?HTMLDivElement;

  handleIndexUpdate = (scrollIntoView?: boolean) => {
    const { indexManager } = this.props;
    this.setState({
      selectedIndex: indexManager.getIndex(),
      selectionMovedByKeyboard: !!scrollIntoView,
    });
  };

  handleSelectItem = (item: Item) => () => {
    const { disabled, onSelectItem } = this.props;
    if (!disabled) {
      onSelectItem(item);
    }
  };

  handleMouseEnter = (sectionRowIndex: SectionRowIndex) => ({
    event,
  }: {
    event: SyntheticMouseEvent<HTMLDivElement>,
  }) => {
    const { disabled, indexManager } = this.props;
    if (!disabled) {
      indexManager.updateIndex(sectionRowIndex);
    }
  };

  handleMouseLeave = ({ event }: { event: SyntheticMouseEvent<HTMLDivElement> }) => {
    const { disabled, indexManager } = this.props;
    if (!disabled) {
      indexManager.setInactive();
    }
  };

  renderItems = (section: Section, sectionIndex: number) => {
    const { renderItem: defaultItemRender } = this.props;
    const { selectedIndex } = this.state;
    const { row: selectedRow, section: selectedSection } = selectedIndex || {};
    const { customProps = {}, items, renderItem: customItemRender } = section;
    const { isDisabled = (item: Item) => false } = customProps;
    const isSectionSelected = sectionIndex === selectedSection;
    const render = customItemRender || defaultItemRender;
    return (
      render &&
      // $FlowFixMe: new errors found from flow 0.96 upgrade
      items.map((item: Item, index: number) => {
        const disabled = isDisabled(item);
        const currentSectionRowIndex = { section: sectionIndex, row: index };
        const selected = isSectionSelected && index === selectedRow;
        return (
          <div
            data-test-id="board-selection"
            key={index}
            // $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
            ref={!disabled && selected && this.setSelectedItemRef}
          >
            {disabled ? (
              render({ item, hovered: selected, sectionProps: customProps })
            ) : (
              <Touchable
                onMouseEnter={this.handleMouseEnter(currentSectionRowIndex)}
                onMouseLeave={this.handleMouseLeave}
                onTouch={this.handleSelectItem(item)}
                fullWidth={false}
                shape="rounded"
              >
                {render({ item, hovered: selected, sectionProps: customProps })}
              </Touchable>
            )}
          </div>
        );
      })
    );
  };

  renderSections = () => {
    const { renderSectionHeader, sections } = this.props;
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    return sections.map((section: Section, index: number) => (
      <Box key={index}>
        {renderSectionHeader && renderSectionHeader(section, index)}
        {this.renderItems(section, index)}
      </Box>
    ));
  };

  render() {
    const { grow = false, maxHeight, fetchMore } = this.props;
    return (
      <ScrollFetcher fetchMore={fetchMore}>
        {({ setRef, onScroll }) => (
          <Box
            flex={grow ? 'grow' : undefined}
            height={grow ? '100%' : undefined}
            maxHeight={maxHeight}
            onScroll={onScroll}
            overflow="auto"
            ref={(ref) => {
              this.containerRef = ref;
              setRef(ref);
            }}
          >
            {this.renderSections()}
          </Box>
        )}
      </ScrollFetcher>
    );
  }
}
